import React, { useState, useEffect } from "react";
import "./ProfileModal.css";
import { useCheckout } from "../context/CheckoutContext";
import { useProfile } from "../context/ProfileContext";

const ProfileModal = ({ user, onLogout, onUpdate }) => {
  const {
    isProfileOpen,
    activeTab,
    closeProfile,
    setActiveTab: setContextTab,
  } = useProfile();

  const { openCheckout, setReorderOrderId, setIsReorder } = useCheckout();

  // STATE
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Update name/phone when user changes
  useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
  }, [user]);

  // Fetch orders when tab is "orders"
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.id || activeTab !== "orders") return;

      setOrdersLoading(true);
      try {
        const response = await fetch(
          `/php/get-customer-orders.php?user_id=${user.id}`
        );
        const data = await response.json();

        if (data.status === "success" && Array.isArray(data.orders)) {
          setOrders(data.orders);
        } else {
          setOrders([]);
        }
      } catch (err) {
        console.error("Failed to load orders:", err);
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [activeTab, user]);

  // Save profile update
  const handleSave = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/php/update-profile.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          name: name.trim(),
          phone: phone.trim(),
        }),
      });

      if (!response.ok) {
        setError(
          response.status === 409
            ? "Phone number is already in use."
            : "Failed to update profile."
        );
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.status === "success") {
        onUpdate({ ...user, name: name.trim(), phone: phone.trim() });
        closeProfile();
      } else {
        setError(data.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const reorder = (order) => {
    if (!order?.id) return;
    setReorderOrderId(order.id);
    setIsReorder(true);
    openCheckout(null, true);
    closeProfile();
  };

  if (!isProfileOpen || !user) return null;

  return (
    <div className="profile-modal-overlay" onClick={closeProfile}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <span className="profile-modal-close" onClick={closeProfile}>
          &times;
        </span>

        {/* Tabs */}
        <div className="profile-tabs">
          <button
            className={activeTab === "profile" ? "active" : ""}
            onClick={() => setContextTab("profile")}
          >
            Profile
          </button>
          <button
            className={activeTab === "orders" ? "active" : ""}
            onClick={() => setContextTab("orders")}
          >
            Order History
          </button>
        </div>

        {/* PROFILE TAB */}
        {activeTab === "profile" && (
          <>
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label>Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {error && <p className="auth-error">{error}</p>}
            <div className="profile-modal-actions">
              <button onClick={handleSave} disabled={loading}>
                {loading ? <span className="spinner"></span> : "Save"}
              </button>
              <button className="logout-btn" onClick={onLogout}>
                Logout
              </button>
            </div>
          </>
        )}

        {/* ORDER HISTORY TAB */}
        {activeTab === "orders" && (
          <div className="order-history">
            {ordersLoading && <p>Loading orders...</p>}
            {!ordersLoading && orders.length === 0 && (
              <p className="no-orders">You haven’t placed any orders yet.</p>
            )}
            {!ordersLoading && orders.length > 0 && (
              <ul className="orders-list">
                {orders.map((order) => (
                  <li
                    key={order.id}
                    className="order-card"
                    onClick={() => toggleOrder(order.id)}
                  >
                    <div className="order-date">
                      {new Date(order.created_at).toLocaleDateString("en-GB")}
                    </div>
                    <div className="order-header">
                      <strong>Order #{order.order_number}</strong>{" "}
                      <span>{order.order_type}</span>
                    </div>
                    <p className="order-total">
                      <strong>£{parseFloat(order.total).toFixed(2)}</strong>
                    </p>

                    {expandedOrderId === order.id && (
                      <div className="profile-order-items">
                        {order.items.map((item) => {
                          const unitPrice =
                            parseFloat(item.base_price || 0) +
                            parseFloat(item.required_option_price || 0);

                          return (
                            <div key={item.id} className="profile-order-item">
                              <div className="profile-order-item-header">
                                <span className="profile-order-item-title">
                                  {item.name}
                                  {unitPrice > 0 &&
                                    ` (£${unitPrice.toFixed(2)})`}
                                </span>
                              </div>

                              {/* Required Option */}
                              {item.required_option_name && (
                                <p className="profile-order-item-option">
                                  {item.required_option_name} (£
                                  {parseFloat(
                                    item.required_option_price
                                  ).toFixed(2)}
                                  )
                                </p>
                              )}

                              {/* Selected Optional Options */}
                              {item.selectedOptionalOptions?.length > 0 && (
                                <div className="profile-order-item-option">
                                  <strong>Optional:</strong>
                                  <ul>
                                    {item.selectedOptionalOptions.map(
                                      (opt, idx) => (
                                        <li key={idx}>
                                          {opt.name} x {opt.quantity} (£
                                          {parseFloat(opt.price).toFixed(2)})
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}

                              {/* Swallow Options */}
                              {item.swallowOptions?.length > 0 && (
                                <div className="profile-order-item-option">
                                  <strong>Swallow:</strong>
                                  <ul>
                                    {item.swallowOptions.map((sw, idx) => (
                                      <li key={idx}>
                                        {sw.name} x {sw.quantity} (£
                                        {parseFloat(sw.price).toFixed(2)})
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Main Proteins */}
                              {item.mainProteins?.length > 0 && (
                                <div className="profile-order-item-option">
                                  <strong>Protein(s):</strong>
                                  <ul>
                                    {item.mainProteins.map((mp, idx) => (
                                      <li key={idx}>
                                        {mp.name} x {mp.quantity} (£
                                        {parseFloat(mp.price).toFixed(2)})
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Extra Proteins */}
                              {item.extraProteins?.length > 0 && (
                                <div className="profile-order-item-option">
                                  <strong>Extra:</strong>
                                  <ul>
                                    {item.extraProteins.map((ep, idx) => (
                                      <li key={idx}>
                                        {ep.name} x {ep.quantity} (£
                                        {parseFloat(ep.price).toFixed(2)})
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              <div className="profile-order-item-total-wrapper">
                                <span className="profile-order-item-total-text">
                                  x {item.quantity} = £
                                  {parseFloat(item.total_price).toFixed(2)}
                                </span>
                              </div>
                              <hr className="profile-order-item-divider" />
                            </div>
                          );
                        })}

                        {/* Order Notes */}
                        {order.notes?.trim() && (
                          <p className="profile-order-notes">
                            <strong>Notes:</strong> {order.notes}
                          </p>
                        )}

                        {/* Reorder Button */}
                        <button
                          className="add-to-cart-button reorder-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            reorder(order);
                          }}
                        >
                          <i className="fas fa-redo"></i> Re-order
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;
